import type { InputHTMLAttributes, SelectHTMLAttributes, TextareaHTMLAttributes } from "react";
import { cn } from "./cn";
import { useField } from "./Field";

const control =
  "w-full rounded-control border border-line-strong bg-surface text-[15px] text-ink placeholder:text-muted/80 " +
  "transition-[border-color,box-shadow] duration-150 ease-out hover:border-ink/25 " +
  "focus-visible:outline-none focus-visible:border-primary focus-visible:ring-3 focus-visible:ring-primary/15 " +
  "aria-invalid:border-bad/70 aria-invalid:focus-visible:ring-bad/15 disabled:opacity-60";

function useControlProps(id?: string, describedBy?: string, invalid?: boolean | "true" | "false" | "grammar" | "spelling") {
  const field = useField();
  return {
    id: id ?? field?.id,
    "aria-describedby": describedBy ?? field?.describedBy,
    "aria-invalid": invalid ?? (field?.invalid || undefined),
  };
}

export type InputProps = InputHTMLAttributes<HTMLInputElement> & { ref?: React.Ref<HTMLInputElement> };

export function Input({ className, id, "aria-describedby": d, "aria-invalid": inv, ...rest }: InputProps) {
  return <input className={cn(control, "h-10 px-3", className)} {...useControlProps(id, d, inv)} {...rest} />;
}

export type SelectProps = SelectHTMLAttributes<HTMLSelectElement>;

export function Select({ className, id, "aria-describedby": d, "aria-invalid": inv, ...rest }: SelectProps) {
  return <select className={cn(control, "h-10 px-2.5", className)} {...useControlProps(id, d, inv)} {...rest} />;
}

export type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement>;

export function Textarea({ className, id, "aria-describedby": d, "aria-invalid": inv, ...rest }: TextareaProps) {
  return <textarea className={cn(control, "min-h-28 resize-y px-3 py-2.5 leading-relaxed", className)} {...useControlProps(id, d, inv)} {...rest} />;
}
